import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock3, FileText, Filter, Save, UserRound, Wrench, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../api";
import type { NonConformity } from "../types";

type NcStatus = NonConformity["status"];

type NcDraft = {
  status: NcStatus;
  responsible: string;
  deadline: string;
  root_cause: string;
  corrective_action: string;
  verification_notes: string;
};

const severityLabel: Record<string, string> = {
  leve: "Leve",
  moderada: "Moderada",
  grave: "Grave",
  gravissima: "Gravíssima"
};

const statusLabel: Record<NcStatus, string> = {
  aberta: "Aberta",
  em_tratamento: "Em tratamento",
  resolvida: "Resolvida"
};

function formatDate(value: string) {
  const normalized = value.includes("T") ? value : value.replace(" ", "T") + "Z";
  const date = new Date(normalized);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("pt-BR", {
        timeZone: "America/Fortaleza",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      }).format(date) + " BRT";
}

export function NCsPage() {
  const [items, setItems] = useState<NonConformity[]>([]);
  const [drafts, setDrafts] = useState<Record<string, NcDraft>>({});
  const [savingId, setSavingId] = useState("");
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState<string[]>([]);
  const [unitFilter, setUnitFilter] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function load() {
    const data = await api<{ ncs: NonConformity[] }>("ncs");
    setItems(data.ncs);

    const next: Record<string, NcDraft> = {};
    for (const nc of data.ncs) {
      next[nc.id] = {
        status: nc.status,
        responsible: nc.responsible || "",
        deadline: nc.deadline || "",
        root_cause: nc.root_cause || "",
        corrective_action: nc.corrective_action || "",
        verification_notes: nc.verification_notes || ""
      };
    }
    setDrafts(next);
  }

  useEffect(() => { load(); }, []);

  const availableVehicleTypes = useMemo(() => {
    const map = new Map<string, { id: string; name: string; code: string; count: number }>();

    for (const nc of items) {
      const id = nc.vehicle_type_id || nc.vehicle_type_code || nc.vehicle_type_name || "sem-tipo";
      const current = map.get(id);
      if (current) {
        current.count += 1;
      } else {
        map.set(id, {
          id,
          name: nc.vehicle_type_name || "Tipo não identificado",
          code: nc.vehicle_type_code || "",
          count: 1
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [items]);

  const availableUnits = useMemo(() => {
    const map = new Map<string, string>();
    for (const nc of items) {
      if (nc.unit_id && nc.unit_name) map.set(nc.unit_id, nc.unit_name);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [items]);

  const filteredItems = useMemo(() => {
    const selected = new Set(selectedVehicleTypes);
    return items.filter((nc) => {
      if (selectedVehicleTypes.length) {
        const id = nc.vehicle_type_id || nc.vehicle_type_code || nc.vehicle_type_name || "sem-tipo";
        if (!selected.has(id)) return false;
      }
      if (unitFilter && nc.unit_id !== unitFilter) return false;
      return true;
    });
  }, [items, selectedVehicleTypes, unitFilter]);

  function toggleVehicleType(typeId: string) {
    setSelectedVehicleTypes((current) =>
      current.includes(typeId)
        ? current.filter((id) => id !== typeId)
        : [...current, typeId]
    );
  }

  function clearVehicleTypeFilters() {
    setSelectedVehicleTypes([]);
  }

  function updateDraft<K extends keyof NcDraft>(id: string, field: K, value: NcDraft[K]) {
    setDrafts((current) => ({
      ...current,
      [id]: {
        ...current[id],
        [field]: value
      }
    }));
  }

  async function saveNc(nc: NonConformity) {
    const draft = drafts[nc.id];
    if (!draft) return;

    setMessage("");
    setError("");

    if ((draft.status === "em_tratamento" || draft.status === "resolvida") && !draft.responsible.trim()) {
      setError("Informe o responsável antes de marcar a não conformidade como Em tratamento ou Resolvida.");
      return;
    }

    setSavingId(nc.id);
    try {
      await api("ncs/" + nc.id, {
        method: "PATCH",
        body: JSON.stringify({
          status: draft.status,
          responsible: draft.responsible.trim() || null,
          deadline: draft.deadline || null,
          root_cause: draft.root_cause.trim() || null,
          corrective_action: draft.corrective_action.trim() || null,
          verification_notes: draft.verification_notes.trim() || null
        })
      });

      setMessage(
        draft.status === "resolvida"
          ? "Não conformidade marcada como resolvida e responsável registrado."
          : draft.status === "em_tratamento"
            ? "Não conformidade colocada em tratamento e responsável registrado."
            : "Não conformidade atualizada."
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível atualizar a não conformidade.");
    } finally {
      setSavingId("");
    }
  }

  return (
    <section>
      <div className="page-title">
        <div>
          <h1>Não conformidades</h1>
          <p>Tratamento, análise de causa, ações corretivas e rastreabilidade dos desvios identificados.</p>
        </div>
        <Link className="primary" to="/nao-conformidades/relatorio">
          <FileText size={17} /> Relatório gerencial
        </Link>
      </div>

      {message && <div className="alert">{message}</div>}
      {error && <div className="alert error">{error}</div>}

      <div className="panel nc-filter-panel">
        <div className="nc-filter-heading">
          <div className="panel-title-icon">
            <Filter size={18} />
            <div>
              <span className="section-kicker">FILTROS</span>
              <h2>Tipo de veículo / equipamento</h2>
            </div>
          </div>

          <div className="nc-filter-summary">
            <strong>{filteredItems.length}</strong>
            <span>de {items.length} NC(s)</span>
          </div>
        </div>

        <p className="muted nc-filter-help">
          Selecione um ou vários tipos ao mesmo tempo e, se desejar, filtre também por unidade.
        </p>

        <label className="nc-unit-filter">
          <span>Unidade</span>
          <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
            <option value="">Todas as unidades</option>
            {availableUnits.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
          </select>
        </label>

        <div className="nc-type-filter-options">
          {availableVehicleTypes.map((type) => {
            const checked = selectedVehicleTypes.includes(type.id);
            return (
              <label className={"nc-type-filter-option " + (checked ? "selected" : "")} key={type.id}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleVehicleType(type.id)}
                />
                <span>
                  <strong>{type.name}</strong>
                  <small>{type.count} NC(s)</small>
                </span>
              </label>
            );
          })}
        </div>

        {selectedVehicleTypes.length > 0 && (
          <button className="secondary nc-clear-filters" type="button" onClick={clearVehicleTypeFilters}>
            <X size={15} /> Limpar filtros ({selectedVehicleTypes.length})
          </button>
        )}
      </div>

      <div className="nc-list">
        {filteredItems.map((nc) => {
          const draft = drafts[nc.id] || {
            status: nc.status,
            responsible: nc.responsible || "",
            deadline: nc.deadline || "",
            root_cause: nc.root_cause || "",
            corrective_action: nc.corrective_action || "",
            verification_notes: nc.verification_notes || ""
          };
          const requiresResponsible = draft.status === "em_tratamento" || draft.status === "resolvida";

          return (
            <article className="panel nc-card" key={nc.id}>
              <div className="nc-card-header">
                <div>
                  <div className="row-gap">
                    <b className="nc-vehicle">{nc.vehicle_identifier}</b>
                    {nc.vehicle_type_name && <span className="nc-vehicle-type-badge">{nc.vehicle_type_name}</span>}
                    <span className="unit-badge">{nc.unit_name || "Unidade não definida"}</span>
                    <span className={"severity " + nc.severity}>{severityLabel[nc.severity] || nc.severity}</span>
                    <span className={"nc-status-badge " + nc.status}>{statusLabel[nc.status]}</span>
                  </div>
                  <p className="nc-description">{nc.description}</p>
                  <small className="nc-created">
                    <Clock3 size={13} /> Registrada em {formatDate(nc.created_at)}
                  </small>
                </div>
              </div>

              <div className="nc-treatment-box">
                <div className="nc-field">
                  <label>Status</label>
                  <div className="input-icon-wrap nc-select-icon">
                    {draft.status === "resolvida"
                      ? <CheckCircle2 size={17} />
                      : draft.status === "em_tratamento"
                        ? <Wrench size={17} />
                        : <Clock3 size={17} />}
                    <select
                      value={draft.status}
                      onChange={(e) => updateDraft(nc.id, "status", e.target.value as NcStatus)}
                    >
                      <option value="aberta">Aberta</option>
                      <option value="em_tratamento">Em tratamento</option>
                      <option value="resolvida">Resolvida</option>
                    </select>
                  </div>
                </div>

                <div className={"nc-field " + (requiresResponsible ? "required-field" : "")}>
                  <label>
                    Responsável pelo tratamento
                    {requiresResponsible && <span className="required-mark"> *</span>}
                  </label>
                  <div className="input-icon-wrap">
                    <UserRound size={17} />
                    <input
                      type="text"
                      placeholder={requiresResponsible ? "Obrigatório" : "Opcional enquanto estiver aberta"}
                      value={draft.responsible}
                      onChange={(e) => updateDraft(nc.id, "responsible", e.target.value)}
                    />
                  </div>
                </div>

                <div className="nc-field">
                  <label>Prazo para tratamento</label>
                  <input
                    type="date"
                    value={draft.deadline}
                    onChange={(e) => updateDraft(nc.id, "deadline", e.target.value)}
                  />
                  <small>Data acordada para conclusão</small>
                </div>

                <div className="nc-field full-nc-field">
                  <label>Causa identificada</label>
                  <textarea
                    placeholder="Descreva a causa provável ou causa raiz identificada para o problema."
                    value={draft.root_cause}
                    onChange={(e) => updateDraft(nc.id, "root_cause", e.target.value)}
                  />
                </div>

                <div className={"nc-field full-nc-field " + (draft.status === "resolvida" ? "required-field" : "")}>
                  <label>
                    Ação corretiva
                    {draft.status === "resolvida" && <span className="required-mark"> *</span>}
                  </label>
                  <textarea
                    placeholder="Descreva o que será ou foi feito para eliminar/corrigir a não conformidade."
                    value={draft.corrective_action}
                    onChange={(e) => updateDraft(nc.id, "corrective_action", e.target.value)}
                  />
                </div>

                <div className="nc-field full-nc-field">
                  <label>Verificação / conclusão</label>
                  <textarea
                    placeholder="Registre evidências, resultado da verificação ou conclusão após a ação."
                    value={draft.verification_notes}
                    onChange={(e) => updateDraft(nc.id, "verification_notes", e.target.value)}
                  />
                </div>

                <button
                  className="primary nc-save-button"
                  type="button"
                  disabled={savingId === nc.id}
                  onClick={() => saveNc(nc)}
                >
                  <Save size={16} />
                  {savingId === nc.id ? "Salvando..." : "Salvar atualização"}
                </button>
              </div>

              {(nc.responsible || nc.deadline) && (
                <div className="nc-current-info">
                  {nc.responsible && (
                    <span><UserRound size={14} /> Responsável atual: <strong>{nc.responsible}</strong></span>
                  )}
                  {nc.deadline && (
                    <span><Clock3 size={14} /> Prazo: <strong>{new Date(nc.deadline + "T12:00:00").toLocaleDateString("pt-BR")}</strong></span>
                  )}
                </div>
              )}
            </article>
          );
        })}

        {!items.length && (
          <div className="panel empty">Nenhuma não conformidade registrada.</div>
        )}

        {items.length > 0 && !filteredItems.length && (
          <div className="panel empty">
            Nenhuma não conformidade encontrada para os tipos de veículo selecionados.
          </div>
        )}
      </div>
    </section>
  );
}
